import React from 'react';
import { Button, Container, Row, Col, Form } from 'react-bootstrap';
import { useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSave } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import InformationModal from '../../Components/InformationModal/InformationModal';
import { CardRouteMapApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import ServerTable from '../../Components/Table/ServerTabe';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'TARJETACREDITO', label: 'Credit Card' },
  { value: 'BANCO', label: 'Bank Account' }
];
const EMPTY_COMPANY_OPTION = { value: null, label: 'Select company...' };

/**
 * @typedef {Object} CardRouteCandidateDto
 * @property {string=} last4
 * @property {string=} accountType
 * @property {string=} accountIdentifier
 * @property {string=} description
 * @property {number|string|null=} mappingId
 * @property {string|null=} costCenterCode
 * @property {string|null=} distributionRule
 * @property {string|null=} cashAccount
 * @property {boolean=} isActive
 */

const normalizeAccountType = (value) => {
  const t = (value ?? '').toString().trim().toUpperCase();
  if (t === 'BANCO') return 'BANCO';
  if (t === 'TARJETACREDITO') return 'TARJETACREDITO';
  return 'TARJETACREDITO';
};

const extractLast4 = (value) => {
  const digits = (value ?? '').toString().replace(/\D/g, '');
  if (!digits) return null;
  return digits.slice(-4);
};

const rowKeyOf = ({ accountType, accountIdentifier, last4 }) => {
  const type = normalizeAccountType(accountType);
  const identifier = accountIdentifier ?? last4 ?? '';
  return `${type}|${identifier}`;
};

const CardRouteMapPageContent = () => {
  const { instance, accounts } = useMsal();

  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);

  const [ccOptions, setCcOptions] = React.useState([]);
  const [drOptions, setDrOptions] = React.useState([]);
  const [cashAccountOptions, setCashAccountOptions] = React.useState([]);
  const [loadingMasters, setLoadingMasters] = React.useState(false);
  const loadedMastersForCompanyRef = React.useRef(new Set());

  const [baseMapCache, setBaseMapCache] = React.useState({});
  const [mapCache, setMapCache] = React.useState({});
  const mapCacheRef = React.useRef({});
  const [dirtyRows, setDirtyRows] = React.useState(new Set());

  const [refreshTick, setRefreshTick] = React.useState(0);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [savingAll, setSavingAll] = React.useState(false);
  const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });

  const [accountTypeFilter, setAccountTypeFilter] = React.useState('');
  const [accountIdentifierFilter, setAccountIdentifierFilter] = React.useState('');

  React.useEffect(() => {
    mapCacheRef.current = mapCache;
  }, [mapCache]);

  const showInfoModal = React.useCallback((title, content) => {
    setInfoModal({ show: true, title, content });
  }, []);

  const hideInfoModal = React.useCallback(() => {
    setInfoModal((prev) => ({ ...prev, show: false }));
  }, []);

  const acquireToken = React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingCompanies(true);
        const token = await acquireToken();
        const arr = await SapMasterApi.getCompanies(token);
        if (cancelled) return;
        const opts = (arr ?? []).map((c) => ({
          value: c.companyId,
          label: c.companyName ?? `Company ${c.companyId}`,
          raw: c
        }));
        setCompanyOptions(opts);
        if (companyId && !opts.some((o) => o.value === companyId)) {
          setCompanyId(null);
          setStoredConciliationCompanyId(null);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load companies', err);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [acquireToken]);

  React.useEffect(() => {
    if (!companyId || loadedMastersForCompanyRef.current.has(companyId)) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingMasters(true);
        const token = await acquireToken();
        const [ccRes, drRes, glRes] = await Promise.all([
          SapMasterApi.getCostCenters(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getDistributionRoutes(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getGlAccounts(companyId, { search: '', page: 1, pageSize: 4000 }, token)
        ]);

        if (cancelled) return;
        setCcOptions((ccRes.items ?? []).map((x) => ({ value: x.centerCode, label: `${x.centerCode} - ${x.centerName ?? ''}` })));
        setDrOptions((drRes.items ?? []).map((x) => ({ value: x.factorCode, label: `${x.factorCode} - ${x.factorDescription ?? ''}` })));
        setCashAccountOptions((glRes.items.filter(x => x.cashAccout == "Y") ?? []).map((a) => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));
        console.log(cashAccountOptions)
        loadedMastersForCompanyRef.current.add(companyId);
      } catch (err) {
        if (!cancelled) console.error('Failed to load masters', err);
      } finally {
        if (!cancelled) setLoadingMasters(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, acquireToken]);

  const findCcOption = React.useCallback((code) => (code ? ccOptions.find((o) => o.value === code) ?? null : null), [ccOptions]);
  const findDrOption = React.useCallback((code) => (code ? drOptions.find((o) => o.value === code) ?? null : null), [drOptions]);
  const findCashOption = React.useCallback(
    (code) => (code ? cashAccountOptions.find((o) => o.value === code) ?? null : null),
    [cashAccountOptions]
  );

  const reconcileDirty = React.useCallback(
    (rowKey, next) => {
      const base = baseMapCache[rowKey] || { costCenterCode: null, distributionRule: null, cashAccount: null, isActive: true };
      const isDirty =
        (base.costCenterCode ?? null) !== (next.costCenterCode ?? null) ||
        (base.distributionRule ?? null) !== (next.distributionRule ?? null) ||
        (base.cashAccount ?? null) !== (next.cashAccount ?? null) ||
        (base.isActive ?? true) !== (next.isActive ?? true);

      setDirtyRows((prev) => {
        const clone = new Set(prev);
        if (isDirty) clone.add(rowKey);
        else clone.delete(rowKey);
        return clone;
      });
    },
    [baseMapCache]
  );

  const fetchData = React.useCallback(
    async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => {
      const token = await acquireToken();

      let search = '';
      if (globalFilter) search = globalFilter;
      columnFilters?.forEach((f) => {
        if (!f?.value) return;
        const id = f.id?.toLowerCase();
        if (id === 'last4' || id === 'accountidentifier' || id === 'accounttype' || id === 'description' || id === 'cashaccount') {
          search = search ? `${search} ${f.value}` : f.value;
        }
      });

      const sortByMap = {
        Last4: 'last4',
        AccountType: 'accountType',
        AccountIdentifier: 'accountIdentifier',
        Description: 'description',
        CashAccount: 'cashAccount'
      };
      const sort = sorting?.[0];
      const sortBy = sortByMap[sort?.id] ?? 'last4';
      const sortDir = sort?.desc ? 'desc' : 'asc';

      const res = await SapMasterApi.getCardLast4Candidates(
        companyId,
        {
          search,
          page: pageIndex + 1,
          pageSize,
          sortBy,
          sortDir,
          accountType: accountTypeFilter || undefined,
          accountIdentifier: accountIdentifierFilter || undefined,
          ...Object.fromEntries(
            (columnFilters ?? [])
              .filter((f) => f?.id === 'CashAccount' && (f?.value ?? '').toString().trim())
              .map((f) => ['CashAccount', (f.value ?? '').toString().trim()])
          )
        },
        token
      );

      const rows = (res.items ?? []).map((r) => {
        const accountType = normalizeAccountType(r.accountType);
        const accountIdentifier = r.accountIdentifier ?? r.last4 ?? null;
        return {
          ...r,
          accountType,
          accountIdentifier,
          last4: r.last4 ?? extractLast4(accountIdentifier),
          rowKey: rowKeyOf({ accountType, accountIdentifier, last4: r.last4 })
        };
      });

      const seededBase = {};
      const seededCur = {};
      rows.forEach((r) => {
        const cur = {
          rowKey: r.rowKey,
          id: r.mappingId ?? null,
          accountType: r.accountType,
          accountIdentifier: r.accountIdentifier,
          cardNumberLast4: r.last4,
          description: r.description ?? null,
          costCenterCode: r.costCenterCode ?? r.mappedCostCenterCode ?? null,
          distributionRule: r.distributionRule ?? r.mappedDistributionRule ?? null,
          cashAccount: r.cashAccount ?? r.CashAccount ?? null,
          isActive: r.isActive ?? r.mappingIsActive ?? true
        };
        seededBase[r.rowKey] = cur;
        seededCur[r.rowKey] = mapCacheRef.current[r.rowKey] ?? cur;
      });

      setBaseMapCache((prev) => ({ ...prev, ...seededBase }));
      setMapCache((prev) => ({ ...seededCur, ...prev }));

      const pageKeys = new Set(rows.map((r) => r.rowKey));
      setDirtyRows((prev) => {
        const next = new Set(prev);
        for (const k of pageKeys) {
          const base = seededBase[k];
          const cur = seededCur[k] ?? base;
          const isDirty =
            (base.costCenterCode ?? null) !== (cur.costCenterCode ?? null) ||
            (base.distributionRule ?? null) !== (cur.distributionRule ?? null) ||
            (base.cashAccount ?? null) !== (cur.cashAccount ?? null) ||
            (base.isActive ?? true) !== (cur.isActive ?? true);
          if (isDirty) next.add(k);
          else next.delete(k);
        }
        return next;
      });

      return {
        items: rows,
        totalItems: res.totalItems ?? res.totalCount ?? 0
      };
    },
    [companyId, acquireToken, accountTypeFilter, accountIdentifierFilter]
  );

  const setCcFor = React.useCallback(
    (rowKey, code) => {
      setMapCache((prev) => {
        const cur = prev[rowKey] ?? { id: null, costCenterCode: null, distributionRule: null, isActive: true };
        const next = { ...cur, costCenterCode: code };
        setTimeout(() => reconcileDirty(rowKey, next), 0);
        return { ...prev, [rowKey]: next };
      });
    },
    [reconcileDirty]
  );

  const setDrFor = React.useCallback(
    (rowKey, code) => {
      setMapCache((prev) => {
        const cur = prev[rowKey] ?? { id: null, costCenterCode: null, distributionRule: null, isActive: true };
        const next = { ...cur, distributionRule: code };
        setTimeout(() => reconcileDirty(rowKey, next), 0);
        return { ...prev, [rowKey]: next };
      });
    },
    [reconcileDirty]
  );

  const setCashFor = React.useCallback(
    (rowKey, code) => {
      setMapCache((prev) => {
        const cur = prev[rowKey] ?? { id: null, costCenterCode: null, distributionRule: null, cashAccount: null, isActive: true };
        const next = { ...cur, cashAccount: code };
        setTimeout(() => reconcileDirty(rowKey, next), 0);
        return { ...prev, [rowKey]: next };
      });
    },
    [reconcileDirty]
  );

  const handleCompanyChange = (opt) => {
    const id = opt?.value ?? null;
    setCompanyId(id);
    setStoredConciliationCompanyId(id);
    setBaseMapCache({});
    setMapCache({});
    setDirtyRows(new Set());
    setRefreshTick((t) => t + 1);
  };

  const openConfirm = React.useCallback(() => {
    if (dirtyRows.size === 0) return;
    setShowConfirm(true);
  }, [dirtyRows]);

  const handleConfirmSaveAll = React.useCallback(async () => {
    setSavingAll(true);
    try {
      const token = await acquireToken();
      const dirtyList = Array.from(dirtyRows);

      const payloads = dirtyList
        .map((k) => ({ rowKey: k, ...mapCache[k] }))
        .filter((p) => !!p.costCenterCode && !!p.cashAccount);

      for (const p of payloads) {
        const accountIdentifier = p.accountIdentifier ?? p.cardNumberLast4 ?? '';
        await CardRouteMapApi.upsertCardRouteMap(
          companyId,
          {
            id: p.id ?? null,
            accountType: normalizeAccountType(p.accountType),
            accountIdentifier,
            cardNumberLast4: p.cardNumberLast4 ?? extractLast4(accountIdentifier),
            costCenterCode: p.costCenterCode,
            distributionRule: p.distributionRule ?? null,
            cashAccount: p.cashAccount ?? null,
            isActive: p.isActive ?? true
          },
          token
        );
      }

      setBaseMapCache((prev) => {
        const clone = { ...prev };
        for (const p of payloads) {
          clone[p.rowKey] = {
            id: p.id,
            accountType: normalizeAccountType(p.accountType),
            accountIdentifier: p.accountIdentifier ?? null,
            cardNumberLast4: p.cardNumberLast4 ?? extractLast4(p.accountIdentifier),
            description: p.description ?? null,
            costCenterCode: p.costCenterCode,
            distributionRule: p.distributionRule ?? null,
            cashAccount: p.cashAccount ?? null,
            isActive: p.isActive ?? true
          };
        }
        return clone;
      });

      setDirtyRows((prev) => {
        const next = new Set(prev);
        for (const p of payloads) next.delete(p.rowKey);
        return next;
      });

      setShowConfirm(false);
      const skipped = dirtyList.length - payloads.length;
      showInfoModal(
        'Mappings saved',
        <div>
          <p className="mb-0"><strong>{payloads.length}</strong> mapping(s) saved successfully.</p>
          {skipped > 0 && <p className="text-muted small mb-0">{skipped} change(s) skipped because Cost Center or Cash Account was not selected.</p>}
        </div>
      );
    } catch (err) {
      console.error('Save all failed', err);
      setShowConfirm(false);
      showInfoModal('Save failed', <p className="mb-0">Some mappings could not be saved. Please try again.</p>);
    } finally {
      setSavingAll(false);
    }
  }, [dirtyRows, mapCache, companyId, acquireToken, showInfoModal]);

  const columns = React.useMemo(
    () => [
      {
        header: 'Type',
        accessorKey: 'accountType',
        id: 'AccountType',
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '110px', minWidth: '110px' },
          filterStyle: { width: '110px', minWidth: '110px' },
          cellStyle: { width: '110px', minWidth: '110px' }
        },
        cell: ({ row }) => (normalizeAccountType(row.original.accountType) === 'BANCO' ? 'Bank Account' : 'Credit Card')
      },
      // {
      //   header: 'Account Identifier',
      //   accessorKey: 'accountIdentifier',
      //   id: 'AccountIdentifier',
      //   cell: ({ row }) => row.original.accountIdentifier ?? row.original.last4 ?? '-'
      // },
      {
        header: 'Last4',
        accessorKey: 'last4',
        id: 'Last4',
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '80px', minWidth: '80px' },
          filterStyle: { width: '80px', minWidth: '80px' },
          cellStyle: { width: '80px', minWidth: '80px' }
        },
        cell: ({ row }) => row.original.last4 ?? extractLast4(row.original.accountIdentifier) ?? '-'
      },
      {
        header: 'Description',
        accessorKey: 'description',
        id: 'Description',
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' },
          filterStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' },
          cellStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' }
        },
        cell: ({ row }) => {
          const value = row.original.description ?? '-';
          return (
            <div className="text-truncate" style={{ maxWidth: 220 }} title={value}>
              {value}
            </div>
          );
        }
      },
      {
        header: 'Cash Account',
        accessorKey: 'cashAccount',
        id: 'CashAccount',
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '320px', minWidth: '320px' },
          filterStyle: { width: '320px', minWidth: '320px' },
          cellStyle: { width: '320px', minWidth: '320px' }
        },
        cell: ({ row }) => {
          const rowKey = row.original.rowKey ?? rowKeyOf(row.original);
          const cached = mapCache[rowKey] || { cashAccount: null };
          return (
            <Select
              classNamePrefix="react-select"
              isDisabled={loadingMasters}
              options={cashAccountOptions}
              value={findCashOption(cached.cashAccount)}
              onChange={(opt) => setCashFor(rowKey, opt?.value ?? null)}
              isClearable
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          );
        }
      },
      {
        header: 'Cost Center',
        id: 'costCenter',
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '320px', minWidth: '320px' },
          filterStyle: { width: '320px', minWidth: '320px' },
          cellStyle: { width: '320px', minWidth: '320px' }
        },
        cell: ({ row }) => {
          const rowKey = row.original.rowKey ?? rowKeyOf(row.original);
          const cached = mapCache[rowKey] || { costCenterCode: null, distributionRule: null, isActive: true };
          return (
            <Select
              classNamePrefix="react-select"
              isDisabled={loadingMasters}
              options={ccOptions}
              value={findCcOption(cached.costCenterCode)}
              onChange={(opt) => setCcFor(rowKey, opt?.value ?? null)}
              isClearable
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          );
        }
      },
      {
        header: 'Distribution Rule',
        id: 'distRoute',
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          headerStyle: { width: '320px', minWidth: '320px' },
          filterStyle: { width: '320px', minWidth: '320px' },
          cellStyle: { width: '320px', minWidth: '320px' }
        },
        cell: ({ row }) => {
          const rowKey = row.original.rowKey ?? rowKeyOf(row.original);
          const cached = mapCache[rowKey] || { costCenterCode: null, distributionRule: null, isActive: true };
          return (
            <Select
              classNamePrefix="react-select"
              isDisabled={loadingMasters}
              options={drOptions}
              value={findDrOption(cached.distributionRule)}
              onChange={(opt) => setDrFor(rowKey, opt?.value ?? null)}
              isClearable
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          );
        }
      }
    ],
    [ccOptions, drOptions, cashAccountOptions, loadingMasters, mapCache, setCcFor, setDrFor, setCashFor, findCcOption, findDrOption, findCashOption]
  );

  const previewItems = React.useMemo(() => {
    const out = [];
    for (const rowKey of Array.from(dirtyRows)) {
      const base = baseMapCache[rowKey] ?? { costCenterCode: null, distributionRule: null, cashAccount: null, isActive: true };
      const cur = mapCache[rowKey] ?? base;
      if (!cur.costCenterCode) continue;
      out.push({
        rowKey,
        accountType: normalizeAccountType(cur.accountType),
        accountIdentifier: cur.accountIdentifier ?? '-',
        last4: cur.cardNumberLast4 ?? extractLast4(cur.accountIdentifier) ?? '-',
        ccFrom: base.costCenterCode ?? '(none)',
        ccTo: cur.costCenterCode,
        drFrom: base.distributionRule ?? '(none)',
        drTo: cur.distributionRule ?? '(none)',
        cashFrom: base.cashAccount ?? '(none)',
        cashTo: cur.cashAccount ?? '(none)',
        active: cur.isActive ? 'Yes' : 'No'
      });
    }
    return out.sort((a, b) => `${a.accountType}-${a.accountIdentifier}`.localeCompare(`${b.accountType}-${b.accountIdentifier}`));
  }, [dirtyRows, baseMapCache, mapCache]);

  const selectedCompany = React.useMemo(() => companyOptions.find((o) => o.value === companyId) ?? null, [companyOptions, companyId]);

  return (
    <Container fluid className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center w-100 mb-2">
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ minWidth: 280 }}>
            <Select
              classNamePrefix="react-select"
              options={[EMPTY_COMPANY_OPTION, ...companyOptions]}
              value={selectedCompany}
              onChange={handleCompanyChange}
              isLoading={loadingCompanies}
              placeholder="Select company..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
        </div>

        <div className="d-flex">
          <button className="btn btn-xl btn-outline-primary fw-bold me-2" onClick={() => setRefreshTick((t) => t + 1)} disabled={!companyId} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} />
          </button>

          <button
            className="btn btn-xl btn-primary fw-bold"
            onClick={() => openConfirm()}
            disabled={dirtyRows.size === 0 || !companyId}
            title={dirtyRows.size ? `Save ${dirtyRows.size} change(s)` : 'No changes to save'}
          >
            <FontAwesomeIcon icon={faSave} className="me-1" />
            Save All
          </button>
        </div>
      </div>

      <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
        <div style={{ minWidth: 220 }}>
          <Select
            classNamePrefix="react-select"
            options={ACCOUNT_TYPE_OPTIONS}
            value={ACCOUNT_TYPE_OPTIONS.find((o) => o.value === accountTypeFilter) ?? ACCOUNT_TYPE_OPTIONS[0]}
            onChange={(opt) => setAccountTypeFilter(opt?.value ?? '')}
            placeholder="Filter by type..."
          />
        </div>
        <div style={{ minWidth: 320 }}>
          <Form.Control
            placeholder="Filter by account identifier..."
            value={accountIdentifierFilter}
            onChange={(e) => setAccountIdentifierFilter(e.target.value)}
          />
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => setRefreshTick((t) => t + 1)}>
          Apply Filters
        </Button>
      </div>

      <Row className="my-3">
        <Col sm={12}>
          {!companyId && (
            <div className="alert alert-info mb-3">
              Select a company to load card/account mapping records.
            </div>
          )}
          <ServerTable
            columns={columns}
            fetchData={fetchData}
            tableClass="table-hover"
            containerClass="table-responsive"
            headerClass="table-light"
            enableGlobalFilter={false}
            initialPageSize={20}
          />
        </Col>
      </Row>

      <ConfirmationModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title="Confirm mappings to save"
        content={
          <div>
            <p className="mb-2">You are about to save <strong>{previewItems.length}</strong> mapping(s).</p>
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Account Type</th>
                    <th>Account Identifier</th>
                    <th>Last4</th>
                    <th>Cost Center (current)</th>
                    <th>Cost Center (new)</th>
                    <th>Route (current)</th>
                    <th>Route (new)</th>
                    <th>Cash Account (current)</th>
                    <th>Cash Account (new)</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItems.map((it) => (
                    <tr key={it.rowKey}>
                      <td>{it.accountType === 'BANCO' ? 'Bank Account' : 'Credit Card'}</td>
                      <td>{it.accountIdentifier}</td>
                      <td>{it.last4}</td>
                      <td>{it.ccFrom}</td>
                      <td>{it.ccTo}</td>
                      <td>{it.drFrom}</td>
                      <td>{it.drTo}</td>
                      <td>{it.cashFrom}</td>
                      <td>{it.cashTo}</td>
                      <td>{it.active}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewItems.length === 0 && <div className="text-muted small mt-2">No valid changes to save (select a Cost Center to proceed).</div>}
          </div>
        }
        onAcceptClick={handleConfirmSaveAll}
        onCancelClick={() => setShowConfirm(false)}
      />
      <InformationModal
        show={infoModal.show}
        onHide={hideInfoModal}
        onAcceptClick={hideInfoModal}
        title={infoModal.title}
        content={infoModal.content}
      />
    </Container>
  );
};

const CardRouteMapPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Card / Account -> Cost Center / Route">
      <CardRouteMapPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default CardRouteMapPage;
